import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  configurationApi,
  MetricDefinitionConfig,
} from '../../services/configuration-api';

interface MetricDefinitionFormProps {
  initialData?: MetricDefinitionConfig;
  onSave: () => void;
  onCancel: () => void;
}

interface FormData {
  name: string;
  description: string;
  formula: string;
  unit: string;
  category: 'dora' | 'velocity' | 'quality' | 'efficiency' | 'delivery';
  enabled: boolean;
  thresholds: {
    good?: number;
    warning?: number;
    critical?: number;
  };
  customProperties: Record<string, any>;
}

export const MetricDefinitionForm: React.FC<MetricDefinitionFormProps> = ({
  initialData,
  onSave,
  onCancel,
}) => {
  const [error, setError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      name: initialData?.name || '',
      description: initialData?.description || '',
      formula: initialData?.formula || '',
      unit: initialData?.unit || '',
      category: initialData?.category || 'delivery',
      enabled: initialData?.enabled ?? true,
      thresholds: {
        good: initialData?.thresholds?.good,
        warning: initialData?.thresholds?.warning,
        critical: initialData?.thresholds?.critical,
      },
      customProperties: initialData?.customProperties || {},
    },
    mode: 'onChange',
  });

  const saveMutation = useMutation({
    mutationFn: (data: MetricDefinitionConfig) =>
      configurationApi.saveMetricDefinition(data),
    onSuccess: () => {
      onSave();
    },
    onError: (error: any) => {
      setError(
        error.response?.data?.error || 'Failed to save metric definition'
      );
    },
  });

  const onSubmit = (data: FormData) => {
    setError(null);

    const metricDefinition: MetricDefinitionConfig = {
      ...data,
      id: initialData?.id,
      createdAt: initialData?.createdAt,
      updatedAt: initialData?.updatedAt,
      createdBy: initialData?.createdBy,
      version: initialData?.version,
    };

    saveMutation.mutate(metricDefinition);
  };

  const categoryOptions = [
    { value: 'dora', label: 'DORA Metrics' },
    { value: 'velocity', label: 'Velocity Metrics' },
    { value: 'quality', label: 'Quality Metrics' },
    { value: 'efficiency', label: 'Efficiency Metrics' },
    { value: 'delivery', label: 'Delivery Metrics' },
  ];

  const commonFormulas = [
    { label: 'Count / Time Period', value: 'count / timeInDays' },
    { label: 'Average Value', value: 'sum(values) / count(values)' },
    { label: 'Percentage', value: '(numerator / denominator) * 100' },
    { label: 'Ratio', value: 'numerator / denominator' },
    { label: 'Rate per Time', value: 'events / timeInHours' },
  ];

  const handleFormulaSelect = (formula: string) => {
    setValue('formula', formula);
  };

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }}>
      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid size={12}>
          <Typography variant='h6' gutterBottom>
            Basic Information
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name='name'
            control={control}
            rules={{ required: 'Name is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label='Metric Name'
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name='category'
            control={control}
            rules={{ required: 'Category is required' }}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.category}>
                <InputLabel>Category</InputLabel>
                <Select {...field} label='Category'>
                  {categoryOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>

        <Grid size={12}>
          <Controller
            name='description'
            control={control}
            rules={{ required: 'Description is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label='Description'
                fullWidth
                multiline
                rows={3}
                error={!!errors.description}
                helperText={errors.description?.message}
              />
            )}
          />
        </Grid>

        {/* Formula Configuration */}
        <Grid size={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant='h6' gutterBottom>
            Formula Configuration
          </Typography>
        </Grid>

        <Grid size={12}>
          <Typography variant='body2' color='text.secondary' gutterBottom>
            Common Formula Templates:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {commonFormulas.map(template => (
              <Button
                key={template.value}
                variant='outlined'
                size='small'
                onClick={() => handleFormulaSelect(template.value)}
              >
                {template.label}
              </Button>
            ))}
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <Controller
            name='formula'
            control={control}
            rules={{ required: 'Formula is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label='Calculation Formula'
                fullWidth
                multiline
                rows={2}
                error={!!errors.formula}
                helperText={
                  errors.formula?.message ||
                  'Use variable names like: deployments, timeInDays, storyPoints, etc.'
                }
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Controller
            name='unit'
            control={control}
            rules={{ required: 'Unit is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label='Unit'
                fullWidth
                error={!!errors.unit}
                helperText={errors.unit?.message}
                placeholder='e.g., per day, percentage, hours'
              />
            )}
          />
        </Grid>

        {/* Thresholds */}
        <Grid size={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant='h6' gutterBottom>
            Performance Thresholds (Optional)
          </Typography>
          <Typography variant='body2' color='text.secondary' gutterBottom>
            Define thresholds for good, warning, and critical performance levels
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Controller
            name='thresholds.good'
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label='Good Threshold'
                type='number'
                fullWidth
                helperText='Values above this are considered good'
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Controller
            name='thresholds.warning'
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label='Warning Threshold'
                type='number'
                fullWidth
                helperText='Values below this trigger warnings'
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Controller
            name='thresholds.critical'
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label='Critical Threshold'
                type='number'
                fullWidth
                helperText='Values below this are critical'
              />
            )}
          />
        </Grid>

        {/* Settings */}
        <Grid size={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant='h6' gutterBottom>
            Settings
          </Typography>
        </Grid>

        <Grid size={12}>
          <Controller
            name='enabled'
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label='Enable this metric'
              />
            )}
          />
        </Grid>

        {/* Actions */}
        <Grid size={12}>
          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}
          >
            <Button onClick={onCancel} disabled={saveMutation.isPending}>
              Cancel
            </Button>
            <Button
              type='submit'
              variant='contained'
              disabled={!isValid || saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
